import React, { useState } from "react";
import {
  HeartPulse,
  Landmark,
  ShoppingCart,
  Factory,
  GraduationCap,
  Truck,
  ArrowRight,
  Sparkles,
  CheckCircle2,
  ChevronRight,
  Briefcase,
} from "lucide-react";

export default function IndustriesPage() {
  const [selectedIndustry, setSelectedIndustry] = useState("Healthcare");

  // Industries dataset
  const industries = [
    {
      id: "Healthcare",
      name: "Healthcare",
      icon: HeartPulse,
      desc: "AI diagnostics, patient management systems, and HIPAA-compliant data pipelines tailored for modern health institutions.",
      highlights: [
        "Predictive Patient Analytics",
        "EHR Integration & Automation",
        "AI Diagnostic Support",
      ],
    },
    {
      id: "Finance",
      name: "Finance",
      icon: Landmark,
      desc: "Secure fintech platforms, automated risk assessment engines, and real-time fraud detection systems.",
      highlights: [
        "Fraud Detection Models",
        "Automated Compliance Engine",
        "Algorithmic Trading & Analytics",
      ],
    },
    {
      id: "Retail",
      name: "Retail",
      icon: ShoppingCart,
      desc: "Next-gen e-commerce optimization, intelligent inventory forecasting, and hyper-personalized shopping experiences.",
      highlights: [
        "AI Recommendation Systems",
        "Smart Inventory Forecasting",
        "Omnichannel Experience Platform",
      ],
    },
    {
      id: "Manufacturing",
      name: "Manufacturing",
      icon: Factory,
      desc: "IoT-driven predictive maintenance, smart factory automation frameworks, and visual quality inspection.",
      highlights: [
        "Predictive Machine Maintenance",
        "Automated Quality Inspection",
        "Supply Chain Optimization",
      ],
    },
    {
      id: "Education",
      name: "Education",
      icon: GraduationCap,
      desc: "Smart Learning Management Systems (LMS), adaptive AI tutors, and comprehensive campus management tools.",
      highlights: [
        "Personalized Adaptive Learning",
        "Automated Assessment Tools",
        "Student Analytics Dashboards",
      ],
    },
    {
      id: "Logistics",
      name: "Logistics",
      icon: Truck,
      desc: "Dynamic route optimization engines, real-time fleet telemetry, and automated warehouse dispatch management.",
      highlights: [
        "Real-Time Fleet Tracking",
        "AI Route Optimization",
        "Automated Warehouse Dispatch",
      ],
    },
  ];

  const currentIndustryData =
    industries.find((ind) => ind.id === selectedIndustry) || industries[0];

  return (
    <div className="bg-slate-50 text-slate-800 font-sans min-h-screen overflow-x-hidden selection:bg-[#f25c05]/20 selection:text-[#f25c05]">
      
      {/* ========================================================== */}
      {/* SECTION 1: HERO / INDUSTRIES OVERVIEW                      */}
      {/* ========================================================== */}
      <section className="bg-white py-12 sm:py-20 lg:py-24 border-b border-slate-100 relative overflow-hidden">
        
        {/* Subtle Ambient Background Blur for Visual Polish */}
        <div className="absolute top-0 right-0 w-72 h-72 sm:w-96 sm:h-96 bg-orange-100/50 rounded-full blur-3xl pointer-events-none -z-10 transform translate-x-1/3 -translate-y-1/3" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            
            {/* Left Column: Heading & Copy */}
            <div className="lg:col-span-5 text-left space-y-4 sm:space-y-6">
              <span className="text-[#f25c05] text-xs sm:text-sm font-extrabold uppercase tracking-widest block">
                INDUSTRIES WE SERVE
              </span>

              <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 leading-[1.15] tracking-tight">
                Intelligent Solutions for Every Industry
              </h1>

              <p className="text-slate-600 text-sm sm:text-base md:text-lg leading-relaxed">
                We deliver domain-specific solutions that address real-world
                challenges and drive measurable results.
              </p>

              <div className="pt-2 sm:pt-4">
                <a
                  href="#deep-dive"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#f25c05] hover:bg-[#d84e00] active:scale-95 text-white font-bold py-3.5 px-7 rounded-xl transition-all duration-200 shadow-lg shadow-[#f25c05]/20 hover:shadow-xl hover:shadow-[#f25c05]/30 focus:ring-4 focus:ring-[#f25c05]/20"
                >
                  View All Industries
                  <ArrowRight size={18} />
                </a>
              </div>
            </div>

            {/* Right Column: Industry Buttons Grid / Carousel */}
            <div className="lg:col-span-7 mt-6 lg:mt-0">
              
              {/* Responsive Grid: 2 columns on small screens, 3 on tablets, 3 on large screens */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 lg:gap-5">
                {industries.map((item) => {
                  const Icon = item.icon;
                  const isSelected = selectedIndustry === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setSelectedIndustry(item.id)}
                      className={`p-4 sm:p-6 rounded-2xl border transition-all duration-300 flex flex-col items-center justify-center text-center gap-3 sm:gap-4 group cursor-pointer active:scale-95 ${
                        isSelected
                          ? "bg-white border-[#f25c05] shadow-xl shadow-orange-500/10 ring-2 ring-[#f25c05]"
                          : "bg-white border-slate-100 hover:border-orange-200 hover:shadow-md"
                      }`}
                    >
                      <div
                        className={`p-3 sm:p-3.5 rounded-xl transition-colors duration-300 ${
                          isSelected
                            ? "bg-[#f25c05] text-white"
                            : "bg-orange-50 text-[#f25c05] group-hover:bg-[#f25c05] group-hover:text-white"
                        }`}
                      >
                        <Icon className="w-6 h-6 sm:w-7 sm:h-7" />
                      </div>
                      <span className="font-bold text-slate-900 text-sm sm:text-base lg:text-lg">
                        {item.name}
                      </span>
                    </button>
                  );
                })}
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* ========================================================== */}
      {/* SECTION 2: DOMAIN-SPECIFIC SOLUTIONS DEEP-DIVE             */}
      {/* ========================================================== */}
      <section id="deep-dive" className="py-12 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-orange-50 text-[#f25c05] text-xs font-bold uppercase tracking-wider mb-3 sm:mb-4">
            <Sparkles size={14} />
            Tailored Capabilities
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-snug">
            Engineered specifically for{" "}
            <span className="text-[#f25c05]">{currentIndustryData.name}</span>
          </h2>
          <p className="mt-2 sm:mt-3 text-slate-600 text-sm sm:text-base md:text-lg">
            Explore how our tech and AI frameworks deliver quantifiable performance gains in this sector.
          </p>
        </div>

        {/* Dynamic Detail Card */}
        <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200/80 p-5 sm:p-8 lg:p-12 shadow-xl shadow-slate-200/50 grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          
          {/* Main Info Area */}
          <div className="lg:col-span-7 flex flex-col justify-between space-y-6">
            <div className="space-y-4 sm:space-y-6">
              <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 bg-slate-900 text-white rounded-lg text-xs sm:text-sm font-bold">
                <Briefcase size={16} className="text-[#f25c05]" />
                {currentIndustryData.name} Industry Brief
              </div>

              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 leading-snug">
                Transforming {currentIndustryData.name} through domain intelligence
              </h3>

              <p className="text-slate-600 leading-relaxed text-sm sm:text-base md:text-lg">
                {currentIndustryData.desc}
              </p>
            </div>

            {/* Feature Bullet Points */}
            <div className="space-y-3 pt-2 sm:pt-4 border-t border-slate-100">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">
                Key Deliverables & Capabilities:
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
                {currentIndustryData.highlights.map((point, i) => (
                  <div key={i} className="flex items-center gap-2.5 text-slate-800 font-semibold text-xs sm:text-sm bg-slate-50 p-2.5 sm:p-3 rounded-xl border border-slate-100">
                    <CheckCircle2 size={18} className="text-emerald-500 shrink-0" />
                    <span className="truncate">{point}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Selector Sidebar */}
          <div className="lg:col-span-5 bg-slate-900 text-white p-5 sm:p-6 lg:p-8 rounded-2xl relative overflow-hidden flex flex-col justify-between">
            <div className="absolute top-0 right-0 w-40 h-40 bg-[#f25c05]/20 rounded-full blur-2xl pointer-events-none" />
            
            <div>
              <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-800">
                <h4 className="text-base sm:text-lg font-bold text-white">
                  Select Sector
                </h4>
                <span className="text-xs font-mono font-bold text-[#f25c05] bg-[#f25c05]/10 px-2.5 py-1 rounded-md">
                  0{industries.findIndex(i => i.id === selectedIndustry) + 1} / 06
                </span>
              </div>

              <div className="space-y-1.5 sm:space-y-2">
                {industries.map((ind) => {
                  const isIndSelected = selectedIndustry === ind.id;
                  return (
                    <button
                      key={ind.id}
                      onClick={() => setSelectedIndustry(ind.id)}
                      className={`w-full text-left px-3.5 py-2.5 sm:py-3 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 flex items-center justify-between group cursor-pointer ${
                        isIndSelected
                          ? "bg-[#f25c05] text-white shadow-md shadow-[#f25c05]/30"
                          : "text-slate-400 hover:text-white hover:bg-slate-800/80"
                      }`}
                    >
                      <span className="truncate">{ind.name}</span>
                      <ChevronRight size={16} className={`transition-transform duration-200 ${isIndSelected ? "translate-x-0.5" : "opacity-50 group-hover:opacity-100"}`} />
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-slate-800 text-xs text-slate-400 flex items-center justify-between">
              <span>Interactive Switcher</span>
              <span className="text-[#f25c05] font-semibold">Instant Render</span>
            </div>
          </div>

        </div>
      </section>

      {/* ========================================================== */}
      {/* SECTION 3: CALL TO ACTION (CTA) BANNER                      */}
      {/* ========================================================== */}
      <section className="bg-slate-950 text-white py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        
        {/* Glow Effects */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] sm:w-[600px] h-[150px] sm:h-[300px] bg-[#f25c05]/20 rounded-full blur-[90px] sm:blur-[120px] pointer-events-none" />

        <div className="max-w-4xl mx-auto text-center relative z-10 space-y-6 sm:space-y-8">
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-black tracking-tight leading-tight">
            Don’t see your industry listed? <br className="hidden sm:inline" />
            <span className="text-[#f25c05] block sm:inline mt-1 sm:mt-0">We build custom solutions.</span>
          </h2>

          <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base md:text-lg leading-relaxed">
            Our engineering team specializes in architecting bespoke AI, Web, and Cloud systems tailored to your unique business parameters.
          </p>

          <div className="flex flex-col sm:flex-row gap-3.5 sm:gap-4 justify-center items-center pt-2 sm:pt-4 max-w-md sm:max-w-none mx-auto">
            <a
              href="/contact"
              className="w-full sm:w-auto bg-[#f25c05] hover:bg-[#d84e00] active:scale-95 text-white font-bold py-3.5 sm:py-4 px-8 rounded-xl transition-all duration-200 shadow-lg shadow-[#f25c05]/25 hover:shadow-xl text-center text-sm sm:text-base"
            >
              Schedule a Consultation
            </a>
            <a
              href="/case-studies"
              className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 active:scale-95 text-slate-300 hover:text-white border border-slate-800 font-semibold py-3.5 sm:py-4 px-8 rounded-xl transition-all duration-200 text-center text-sm sm:text-base"
            >
              Explore Case Studies
            </a>
          </div>
        </div>
      </section>

    </div>
  );
}